/**
 * Site chrome: navigation, footer, company details, social links.
 *
 * One file so there is exactly one place to change a link, and so a route that
 * gets renamed cannot leave a dead link in the footer of every page.
 *
 * Since the split into three sites (`src/lib/sites.ts`), navigation and footer
 * are per site rather than global. The store's chrome belongs to a player
 * looking for a game; the creators' and developers' chrome belongs to someone
 * at work. They share a wordmark and a legal row and almost nothing else.
 *
 * **Social handles and every contact address are unset on purpose.** Section
 * 29.2: ask rather than invent. Inventing a plausible-looking Discord invite or
 * support address produces a footer that looks finished and sends players
 * nowhere, which is worse than a footer that is visibly still being filled in.
 * Anything left null here simply does not render — see `configuredSocials`.
 *
 * There used to be one exception, and #142 removed it: the publishing address,
 * which was real because #125 had made it the only way a studio could ask to be
 * published. Nobody is being published, so it is an address for a conversation
 * we are not having.
 */

import type { SiteId } from './sites';

/**
 * The public domain, from section 1 of the specification.
 *
 * Used for canonical URLs, Open Graph tags and the widget embed origin. Worth
 * keeping in one constant: an origin that disagrees between the store, the
 * embed snippets and the API is the kind of thing nobody notices until a
 * studio's widget stops loading.
 */
export const SITE_ORIGIN = 'https://goodbuild.cc';

export const SITE_NAME = 'GOODBUILD';

/**
 * Section 1. The positioning line, used verbatim rather than paraphrased.
 *
 * **We are a game studio, and this line has stopped describing a shop.** It
 * has been wrong three times, each time by naming a role the company was not
 * filling. "The store where creators are the storefront" was the open
 * marketplace, where any studio could list. "An indie games publisher" was the
 * period when we meant to sign other people's games. "We make the games. We
 * sell them here." was half a sentence about the games and half a sentence
 * about the till.
 *
 * Three words, and the till is not in them. Where somebody pays is a detail of
 * this site; what the company *is* is the thing a first-time visitor has to be
 * told, and the second clause was explaining a checkout to somebody who had
 * not yet been told what we make.
 *
 * It renders as a display headline on an empty catalogue and as the line under
 * the wordmark in every footer. Both are places where the shorter one is the
 * louder one.
 */
export const POSITIONING = 'We make games.';

/**
 * The line under the positioning, and it is about us rather than about a page.
 *
 * It was `Written by the people who played it.` for three repositionings —
 * true, and a sentence about how a game page is written. That argument has a
 * home on `/about`, where there is room to show what it means. As the second
 * line anybody ever reads it was answering a question nobody had asked, to
 * somebody who had just been told we make games and wanted to know who *we*
 * were.
 *
 * Short words on purpose, here and everywhere else a player reads. Nothing on
 * this site needs a reading age.
 */
export const TAGLINE = 'A small team. Buy them straight from us.';

/**
 * Who operates GOODBUILD.
 *
 * A storefront selling to EU consumers has to identify its trader, so this is
 * a legal requirement rather than a design flourish. Section 8 of the
 * specification puts GOODBUILD's merchant-of-record decision under review, so
 * the wording here says who operates the platform and stops short of stating
 * who the merchant of record is — that line can only be written once the
 * decision is made.
 *
 * **Not Victor Game Studio.** VGS makes games, some of which compete with
 * titles listed here; GOODBUILD is operated by a separate Polish entity. The
 * brand guidelines already argue the visual independence is a commercial
 * requirement rather than a preference, and the operating entity is the same
 * argument in company law: a platform asking studios to trust its attribution
 * numbers should not be the studio they are competing with.
 *
 * **The identifiers are the ones CEIDG actually issues, and there is no
 * company number among them.** This is a sole trader — a wpis in CEIDG rather
 * than a company in the KRS — and CEIDG gives an entry no number of its own.
 * What identifies it is the NIP and the REGON, so those are what is stated and
 * `registrationNumber` stays null. Filling it with something plausible would be
 * worse than leaving it out, because it is the field a buyer would use to check
 * who they are dealing with.
 *
 * `vatNumber` is null because **there is no VAT number**, which is a different
 * thing from not knowing one. An EU VAT number is `PL` in front of the NIP only
 * for an entity registered for VAT, and this one is not registered yet
 * (confirmed 2026-08-26). Writing `PL9512645271` here would state a number that
 * does not exist — the same mistake as a plausible company number, on the field
 * a business buyer would try to validate.
 *
 * Nothing on this platform depends on it. Creem is the merchant of record and
 * charges the tax on every sale in its own name, so the VAT a buyer pays is
 * Creem's to account for rather than ours. Registering later means setting this
 * one value and nothing else here.
 *
 * `configuredCompanyDetails` renders only what is set.
 */
export const COMPANY = {
  /** Exactly as CEIDG spells it, comma included. */
  legalName: 'Victor Chanet Software, Retail and Consulting',
  /** Country of registration, stated because the trader is not local to most buyers. */
  country: 'Poland',
  operatorLine:
    'GOODBUILD is run by Victor Chanet Software, Retail and Consulting, a sole trader registered in Poland.',
  /** CEIDG issues an entry no number. The two below are what identify it. */
  registrationNumber: null as string | null,
  /** NIP, the tax identification number. */
  taxNumber: '9512645271' as string | null,
  /** REGON, the statistical number. */
  statisticalNumber: '544392504' as string | null,
  /** No VAT number: not registered yet, rather than not known. See above. */
  vatNumber: null as string | null,
  /** The permanent place of business, as it appears on the register. */
  registeredAddress: 'ul. Marcina Kasprzaka 31/119, 01-234 Warszawa, Poland' as string | null,
} as const;

/** Only the trader identifiers that actually exist. */
export function configuredCompanyDetails(): { label: string; value: string }[] {
  const fields: [keyof typeof COMPANY, string][] = [
    ['registrationNumber', 'Company number'],
    ['taxNumber', 'Tax number (NIP)'],
    ['statisticalNumber', 'REGON'],
    ['vatNumber', 'VAT number'],
    ['registeredAddress', 'Registered address'],
  ];

  return fields
    .filter(
      (entry): entry is [keyof typeof COMPANY, string] => typeof COMPANY[entry[0]] === 'string',
    )
    .map(([key, label]) => ({ label, value: COMPANY[key] as string }));
}

/**
 * Where to reach a human.
 *
 * Null until a real address exists. A contact page that lists an address
 * nobody reads is worse than one that says the channel is not open yet.
 *
 * **`general` is set, and the three legal documents are why.** A privacy policy
 * has to name somewhere a person can send a data request, and the terms of use
 * have to name somewhere a buyer can complain — so the choice was no longer
 * between an address and no address, it was between one address and three
 * documents pointing at nothing. It is the same address `EMAIL_FROM` sends
 * from, deliberately: a reply to anything this platform sends lands where the
 * documents say it will.
 *
 * It has to actually receive. Sending from an address does not make it an
 * inbox, and an unrouted `hello@` is the failure this file's whole argument is
 * about, one step later.
 */
export const CONTACT = {
  /** Support, and the address every legal document names. */
  general: 'hello@goodbuild.cc' as string | null,
  /**
   * Studios asking to publish with us — **removed** (#142).
   *
   * It was the one address here that was real. #125 shut the self-serve studio
   * registration and pointed everybody at `publishing@goodbuild.cc` instead, so
   * listing it was the difference between a door and a door somebody had to be
   * told about.
   *
   * There is no door now. We are not a publisher and no third-party studio is
   * coming onto the platform, so an inbox for pitches collects mail nobody can
   * say yes to — which is worse than the absent channel section 29.2 argues
   * for, because a person on the other end spent an evening writing to it.
   *
   * The key is kept rather than deleted so `configuredContacts` still has a
   * label for it if the conversation ever reopens, and so that reopening it is
   * one line here rather than a shape somebody has to rebuild.
   */
  developers: null as string | null,
  /** Press. Section 21.3 makes press kits a first-class surface. */
  press: null as string | null,
  /** Security disclosures. Worth a dedicated address from day one. */
  security: null as string | null,
} as const;

/**
 * The Discord invite, from the environment.
 *
 * The community link, wherever a surface offers one — today that is the
 * footer's social row. It is configuration rather than a constant because dev
 * and production point at different servers, and because an invite gets revoked
 * and reissued without anybody wanting to ship a release for it.
 *
 * It used to be the single most load-bearing URL on the site, back when the
 * front page was a waitlist and the server's member count was the gate. Nothing
 * gates on it now, so an unset invite costs a link in a footer rather than the
 * only action the site asked for.
 *
 * **A function, not a constant, and that is load-bearing.** On Workers,
 * `@opennextjs/cloudflare` copies the bindings into `process.env` at the top of
 * every request. A module-level `process.env.DISCORD_INVITE` is evaluated when
 * the isolate first imports this file, which is before any request has arrived,
 * and would read `undefined` forever. Everything that renders it is
 * server-rendered, so reading it per render is free and always current.
 *
 * Unset is a supported state, not a broken one. Section 29.2: ask rather than
 * invent. A missing or malformed value renders no button at all, which is
 * better than a button that 404s on the one action the launch gate depends on.
 */
export function discordInvite(env?: EnvSource): string | null {
  return httpsUrl(read(env, 'DISCORD_INVITE') ?? read(env, 'NEXT_PUBLIC_DISCORD_INVITE'));
}

/**
 * Where the value comes from, when `process.env` is not it.
 *
 * The third application again. The Next Worker has bindings copied into
 * `process.env` by the OpenNext adapter, so it passes nothing. Astro puts them
 * on `Astro.locals.runtime.env` and nowhere else, so the storefront passes that
 * object in. Guessing would mean the storefront rendering no join button and
 * nothing saying why.
 */
export type EnvSource = Record<string, unknown> | undefined;

function read(env: EnvSource, name: string): string | undefined {
  const value = env?.[name] ?? readEnv(name);
  if (typeof value !== 'string') return undefined;
  return value.trim() === '' ? undefined : value.trim();
}

/**
 * One variable, read wherever the host happens to keep it.
 *
 * `process` is absent in a browser bundle, so this is guarded rather than
 * assumed. That is also the honest limit of it: the Astro storefront and the
 * React app read `import.meta.env`, which this cannot see, so their footers
 * show no Discord link until they are given the value their own way, by passing
 * their own bindings object in. Anywhere `process.env` is populated, a `vars`
 * entry and a secret both land there and both work.
 */
function readEnv(name: string): string | undefined {
  if (typeof process === 'undefined' || !process.env) return undefined;
  const value = process.env[name];
  return value === undefined || value.trim() === '' ? undefined : value.trim();
}

/**
 * An absolute https URL, or nothing.
 *
 * A typo in a deployment variable should cost a missing button, never a link
 * that goes somewhere unintended. `javascript:` and protocol-relative values
 * are rejected here rather than at the point somebody clicks them.
 */
function httpsUrl(value: string | undefined): string | null {
  if (value === undefined) return null;
  try {
    return new URL(value).protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Where the desktop launcher can be downloaded, per platform.
 *
 * The launcher is how a player installs and updates what they own — chunked
 * patches, verified builds, a machine list they can revoke from their account —
 * and there was no page in the product that offered it. The store sold games
 * and then said nothing about how to run one.
 *
 * **Configuration rather than constants, and null until a build exists.**
 * Section 29.2: ask rather than invent. A plausible `/downloads/GOODBUILD.exe`
 * is worse than an absent one, because the page reads as finished and the one
 * action it exists for 404s — and this is the last download on earth where that
 * is survivable, since the file it fails to fetch is the thing that installs
 * games. `Cta` draws a null href as a button marked unavailable, so the shape
 * of the page is the same either way and only the link is missing.
 *
 * A function rather than a constant, for the same reason `discordInvite` is
 * one: a module-level `process.env` read is evaluated when the isolate first
 * imports this file, which on Workers is before any request has arrived.
 *
 * Signed installers live wherever the release is published — R2, a GitHub
 * release — and that is deliberately not decided here. What this file owns is
 * that the page cannot invent one.
 *
 * ## One platform, and it is still a list
 *
 * macOS and Linux were entries here and are gone. Neither was ever going to
 * carry a URL: Authenticode through Azure covers Windows only, and an unsigned
 * macOS build is refused by Gatekeeper on every machine that downloads it — so
 * the two of them drew permanently unavailable buttons on the one page whose
 * entire job is handing over a file. Section 29.2 says an absent channel beats
 * a dead one, and a button that has never worked and cannot is the deadest
 * kind: it reads as *this product is half-finished* rather than as *this
 * product is for Windows*.
 *
 * **The array stays an array.** A second platform is one entry and one
 * environment variable, `GET /v1/site` keeps the shape its client already
 * parses, and nothing has to be rebuilt on the day there is a signing path for
 * macOS. What the download page must not do is assume a length — it renders
 * the first entry as the one download and says which platform it is for.
 */
export interface LauncherBuild {
  /** The platform, as somebody would recognise their own. */
  label: string;
  /** What the file actually is. A download should not be a surprise. */
  note: string;
  /** Null until a build for this platform is published. */
  url: string | null;
}

export function launcherBuilds(env?: EnvSource): readonly LauncherBuild[] {
  return [
    {
      label: 'Windows',
      note: 'Windows 10 or newer, 64-bit.',
      url: httpsUrl(read(env, 'LAUNCHER_URL_WINDOWS')),
    },
  ];
}

/**
 * Which release the buttons above point at, when it is known.
 *
 * Shown beside the downloads rather than inferred from a filename, because a
 * version somebody can read is the difference between "I have the latest" and
 * downloading it again to find out. Unset renders nothing, which is the honest
 * state before there is a release to name.
 */
export function launcherVersion(env?: EnvSource): string | null {
  const value = read(env, 'LAUNCHER_VERSION');
  return value ?? null;
}

export interface CliDownload {
  version: string;
  url: string;
}

export function cliDownload(env?: EnvSource): CliDownload | null {
  const url = httpsUrl(read(env, 'GBCLI_URL_WINDOWS'));
  const version = read(env, 'GBCLI_VERSION');
  if (url === null || version === undefined) return null;
  return { version, url };
}

export interface SocialLink {
  label: string;
  /** Null until the account exists. Unset links do not render. */
  url: string | null;
}

/**
 * Social accounts.
 *
 * Discord is listed first deliberately: section 7.4 treats Discord as the
 * place a studio's team already lives, and it is where this platform's
 * audience will expect to find it.
 */
export const SOCIALS: readonly SocialLink[] = [
  // Discord's url stays null here and is filled by `configuredSocials` from the
  // environment. This array is a module constant, and a module constant cannot
  // read a Worker binding — see `discordInvite`.
  { label: 'Discord', url: null },
  { label: 'Bluesky', url: null },
  { label: 'Mastodon', url: null },
  { label: 'YouTube', url: null },
  { label: 'Twitch', url: null },
  { label: 'GitHub', url: null },
];

/**
 * Only the accounts that actually exist.
 *
 * Discord's URL is resolved here rather than in `SOCIALS` so that every surface
 * offering the invite reads the same environment variable at the same moment.
 * Two sources for one invite is how a footer ends up pointing at a server
 * nobody else on the site is pointing at.
 */
export function configuredSocials(env?: EnvSource): { label: string; url: string }[] {
  const invite = discordInvite(env);

  return SOCIALS.map((social) =>
    social.label === 'Discord' ? { ...social, url: invite } : social,
  ).filter((social): social is { label: string; url: string } => typeof social.url === 'string');
}

export function supportAddress(): string | null {
  return CONTACT.general;
}

/** Only the contact addresses that actually exist. */
export function configuredContacts(): { role: string; address: string }[] {
  const labels: Record<keyof typeof CONTACT, string> = {
    general: 'Support',
    // Unset since #142 and so never rendered. The label stays because the key
    // does — see `CONTACT.developers`.
    developers: 'Publishing a game',
    press: 'Press',
    security: 'Security',
  };

  return Object.entries(CONTACT)
    .filter((entry): entry is [keyof typeof CONTACT, string] => typeof entry[1] === 'string')
    .map(([key, address]) => ({ role: labels[key], address }));
}

export interface NavItem {
  /**
   * A path, always, and never an origin.
   *
   * **Half of these used to be `siteUrl('developers', '/portal')` and the
   * like**, on the reasoning that a cross-site entry has to be absolute or it
   * resolves against the wrong host. The reasoning was right and the origin it
   * built was not: `siteUrl` answers a production hostname, or — when
   * `NODE_ENV` is development — a table of ports the retired Next monolith
   * served its three sites on. Nothing has listened on them since, so in a
   * `pnpm dev` session `Get published`, `Developer portal`, `Creator
   * programme`, `Opportunity board` and every legal link in the footer went to
   * a refused connection.
   *
   * One deployment answers for every path in this file, so a path is all any of
   * them has to be and nothing has to work out where to point it. An entry that
   * genuinely names another origin — a Discord invite, a partner — arrives
   * absolute and `isCrossSite` is what tells `ChromeLink` to leave it alone.
   */
  href: string;
  label: string;
  /**
   * Which row of the top bar this entry belongs on. Ignored everywhere else.
   *
   * **The bar is two rows for somebody signed in, and it was drawing every
   * entry on the loud one.** That row answers *where can I go*, at three times
   * the size of anything else in the header, and `Launcher` and `Admin` are not
   * answers to it: launcher settings are a preference panel, and the staff
   * console is a tool that happens to be reachable from a shop. Set beside
   * `Games` and `Library` at 2.25rem they made the wayfinding row a list of
   * things of two different kinds, and the destinations that actually name the
   * product were the minority of it.
   *
   * So an entry can say it is furniture rather than a destination, and the bar
   * draws it small, up beside the mark, with `Install launcher` — the row that
   * already holds the things that are not places.
   *
   * A field on the entry rather than a second function returning a second list,
   * because the order is one order: `topNav` is the argument about what this
   * account can open, `site-chrome.test.ts` sweeps its result for dead links
   * and overlapping prefixes, and splitting it in two would give that sweep two
   * lists to remember. `navSlots` below does the dividing, from the one list.
   *
   * Optional, and absent means `primary`. Every footer column, every rail
   * section and every entry an application builds by hand goes on rendering
   * exactly what it rendered before.
   */
  slot?: NavSlot | undefined;
}

/**
 * Which row of the top bar an entry is drawn on.
 *
 *   primary   a destination — somewhere the product *is*. The loud row.
 *   utility   furniture — a tool, a console. The small row, beside
 *             the mark.
 *
 * Only the signed-in bar has two rows to divide between. Signed out there are
 * four things in the whole header, so they sit on one line at one weight and
 * this distinction has nothing to do; see `SiteNav`.
 */
export type NavSlot = 'primary' | 'utility';

/**
 * The bar's entries, split into the two rows that draw them.
 *
 * Here rather than in the component so that both halves come from one pass over
 * one list: written as two `filter` calls at the use site, an entry whose slot
 * is neither value would silently appear in neither row — a link that exists,
 * resolves, passes every sweep in `site-chrome.test.ts`, and is drawn nowhere.
 * The default lives in one place and it defaults *towards* being visible.
 *
 * Order is preserved within each row, because `topNav` argues an order and this
 * is a division of it rather than a re-sort.
 */
export function navSlots(items: readonly NavItem[]): {
  primary: readonly NavItem[];
  utility: readonly NavItem[];
} {
  const primary: NavItem[] = [];
  const utility: NavItem[] = [];

  for (const item of items) {
    if (item.slot === 'utility') utility.push(item);
    else primary.push(item);
  }

  return { primary, utility };
}

/**
 * Whether an entry names its own origin and must be left alone.
 *
 * Nothing in `SITE_CHROME` does any more; `configuredSocials` and anything a
 * caller builds still can. It also decides what `ChromeLink` renders, because a
 * client-side router cannot navigate across an origin.
 */
export function isCrossSite(href: string): boolean {
  return href.startsWith('http://') || href.startsWith('https://');
}

/**
 * **`chromeHref` and `ChromeOrigins` used to live here, and they are gone.**
 *
 * They existed because the storefront and the signed-in app were separate
 * Workers on separate hosts, while `SITE_CHROME` below held relative paths
 * shared by both — so every entry was correct on one host and a 404 on the
 * other, and which was which depended on the entry. `chromeHref` prefixed each
 * one with the origin of whichever surface served it.
 *
 * One deployment serves all of them again. Every path in this file resolves
 * against whatever host answered, which is what a relative href does on its
 * own, and a resolver with nobody to resolve for is a hop somebody would have
 * to reason about for no benefit. `isCrossSite` stays: a Discord invite in the
 * social row is still an absolute URL, and `ChromeLink` still has to know not to
 * hand one to a client-side router.
 *
 * `isAppPath` in `@goodbuild/rules/sites` was the list this asked, and it
 * survives with a narrower job: it is the session map now, and the app's router
 * is what reads it.
 */

export interface FooterColumn {
  title: string;
  items: readonly NavItem[];
}

/**
 * What an account can open, as far as the top navigation is concerned.
 *
 * Three booleans rather than `AccountStanding` from core, and the difference is
 * deliberate. That type distinguishes `applied` from `active` from `suspended`,
 * because the account page has to be able to say which — and a navigation bar
 * has one question per role, which is whether to draw the link. An applied
 * creator cannot open the board, so as far as this file is concerned they are
 * not a creator; the account page is where they read why.
 */
export interface NavStanding {
  signedIn: boolean;
  /** An approved creator. The board is closed to an applied one — section 3. */
  creator: boolean;
  /*
    `developer` was here and went with the studios (#144). It drew a
    `Developers` entry pointing at `/portal`, which was a studio's own screens.
    `USER_ROLES` no longer has the role and there is no portal to point at.
  */
  admin: boolean;
  /**
   * Whether this is the desktop launcher rather than a browser.
   *
   * **The one field here that is not a role, and the one that never travels in
   * the session hint.** The other three are facts about the account and the
   * storefront learns them from the cookie; this is a fact about the *program
   * doing the asking*, and no cookie could carry it honestly — the same account
   * is in the launcher on one machine and in a browser on another, at the same
   * moment.
   *
   * So it is answered at the point of asking: `isLauncher()` in the React app,
   * which reads whether the Tauri IPC bridge exists. The storefront never sets
   * it, and must not — a web player offered `Downloads` would get two screens
   * whose every button calls a bridge that is not there.
   *
   * Optional for that reason. Absent means a browser, which is the safe way to
   * be wrong: a link too few on the desktop costs a detour, a link too many on
   * the web is a door onto a broken room.
   */
  launcher?: boolean | undefined;
}

/** What the chrome assumes when nothing has told it otherwise. */
export const SIGNED_OUT: NavStanding = Object.freeze({
  signedIn: false,
  creator: false,
  admin: false,
});

/**
 * The top navigation, which is one bar on every surface.
 *
 * **It used to be three bars, and which one you got came from the hostname.**
 * The store carried `Games`, `Publishing`, `Library` and `Orders`; the creators
 * site carried `The programme` and `Board`; the developers site carried
 * `Publishing` and `Portal`. (`Publishing` was the pitch page, and it is gone
 * everywhere since #142.) So a studio in its own portal was reading the
 * player's bar — the React app is one deployment on `app.goodbuild.cc`, and
 * `resolveSite` answers `store` for every page it serves — while a creator on
 * `creators.goodbuild.cc` was reading a third thing again. Three answers to
 * "where can I go", none of them the same, and none of them a function of who
 * was asking.
 *
 * One bar, and what is in it is what this account can actually open:
 *
 *   Games      everyone, signed in or not. The catalogue is the product.
 *   Library    everyone signed in. What they own.
 *   Creators   an approved creator, to the board.
 *   Developers a studio, to the portal.
 *   Admin      staff. Absent entirely otherwise — there is nothing to apply
 *              for and no queue, so a greyed link would be an offer we do not
 *              make.
 *
 * **The list is one list and the bar draws it on two rows**, which is what
 * `NavItem.slot` decides and `navSlots` divides. `Games`, `Library`,
 * `Downloads` and `Creators` are destinations and go on the loud row;
 * `Launcher` and `Admin` are furniture and go up beside the mark with
 * `Install launcher`. Nothing about *what this account can open* changes with
 * the slot — the argument above is still the whole of it — and the order here
 * is still the order each row is drawn in.
 *
 * **`Orders` is not here.** It is on the account page, beside the address, the
 * sessions and the roles, which is where somebody looks for what they bought.
 * It is still in the footer.
 *
 * `Publishing` was not here either, on the argument that a pitch is read once
 * before a decision and a permanent seat in the furniture of a shop is the
 * wrong shape for a page nobody needs twice. That argument is retired rather
 * than won: #142 removed the page, so there is nothing to keep out of the bar.
 *
 * **A role link is not a permission**, and none of these is trusted for
 * anything. Every one of them leads to the React app, which asks the API for a
 * real session and refuses without one. That is what makes it safe for the
 * storefront to draw this bar from a cookie hint — see `lib/session-hint`.
 */
export function topNav(standing: NavStanding): readonly NavItem[] {
  const items: NavItem[] = [{ href: '/', label: 'Games' }];

  // Everything below is behind a session, so a signed-out visitor gets the
  // catalogue and the sign-in link beside it rather than four doors that ask
  // who they are. Section 29: the platform has to be readable before it is
  // joinable.
  if (!standing.signedIn) return items;

  items.push({ href: '/library', label: 'Library' });

  /*
   * The desktop's two extra rooms, next to the library because that is what
   * they are about: what you own, and getting it onto this machine.
   *
   * Above the role links on purpose. Somebody running the launcher to play a
   * game is doing the same thing every other player is doing, whatever else
   * their account can reach.
   *
   * These are the only entries in this bar that are not a function of the
   * account, which is why `launcher` is optional and why the storefront leaves
   * it unset — see `NavStanding.launcher`.
   */
  if (standing.launcher) {
    items.push({ href: '/downloads', label: 'Downloads' });
    /*
     * The two of them are not the same kind of thing, which the slots now say.
     * `Downloads` is a room — what is on this machine and what is coming — and
     * it belongs beside `Library` because it is the same question asked of the
     * disk instead of the account. `Launcher` is the preference panel for the
     * program you are inside, and a preference panel is furniture.
     */
    items.push({ href: '/settings/launcher', label: 'Launcher', slot: 'utility' });
  }

  // Named for the side of the product rather than for the page: `Creators` is
  // what `tokens.css` calls the space and what the tab icon changes to on
  // arrival, so the bar and the room agree. `Developers`, pointing at
  // `/portal`, was beside it until #144.
  if (standing.creator) items.push({ href: '/board', label: 'Creators' });
  /*
   * Staff, and furniture rather than a destination.
   *
   * `Creators` is a place the product is — a board of work, which is the whole
   * of what that side of the product offers the person who can open it. The
   * staff console is a tool for running the shop, and the handful of people who
   * hold it are not helped by it being the loudest word in their header on
   * every page of the catalogue. It stays absent entirely for everybody else,
   * which is the part that matters and has not changed.
   */
  if (standing.admin) items.push({ href: '/admin', label: 'Admin', slot: 'utility' });

  return items;
}

export interface SiteChrome {
  /** The line under the wordmark in the footer. */
  footerLine: string;
  columns: readonly FooterColumn[];
}

/**
 * The footer, per site.
 *
 * **The navigation used to be here too, and is not any more.** `topNav` above
 * is one bar for all three sites, keyed on who is asking rather than on which
 * hostname answered — see the note there for what three bars cost. What is left
 * is genuinely per site: the line under the wordmark and the columns, which are
 * an editorial choice about what each audience is offered on the way out.
 *
 * Every entry is a path, never an origin — see `NavItem.href` for the dev
 * session this cost. One deployment answers for all of them, so a path resolves
 * against whichever host is serving the page it is drawn on and there is
 * nothing to work out.
 *
 * Every entry must also be a route that exists — `test/site-chrome.test.ts`
 * enforces it. A footer entry pointing at a filter nothing implements is a dead
 * link dressed as a feature.
 */
export const SITE_CHROME: Readonly<Record<SiteId, SiteChrome>> = {
  /**
   * The store's footer belongs to the person who came here to find a game, and
   * offers the other two audiences a column each on the way out.
   *
   * **There is no `Studios` column any more** (#141, #142, #143). It held
   * `Get published` and `Developer portal`, and both were offers to somebody
   * else's studio: the first is a pitch page that no longer exists, and the
   * second is a workplace that `topNav` already puts in the bar for anybody who
   * actually holds the role. A column soliciting studios on the site of a
   * company that does not sign them is furniture around an empty room.
   *
   * **`Brand` is in `Company` on all three sites, and the footer is the right
   * place for it rather than the bar.** It is the page a creator opens once
   * before putting our mark in a video, a studio opens once while writing an
   * announcement, and a journalist opens once — a reference somebody goes
   * looking for, which is exactly what a footer is for. Nobody needs it twice
   * and nobody should have to ask us for a logo.
   */
  store: {
    footerLine: POSITIONING,
    columns: [
      {
        title: 'Games',
        items: [
          /*
            `Our games`, not `Games we publish` (#141). Every title on that page
            is one we made, and the old label described the arrangement rather
            than the catalogue.

            It points at `/` rather than `/browse` because the catalogue is the
            front page now. `/browse` still redirects here, but a footer link
            should name the address it means.
          */
          { href: '/', label: 'Our games' },
          { href: '/download', label: 'Get the launcher' },
          { href: '/library', label: 'Your games' },
          { href: '/orders', label: 'What you bought' },
          /*
            A fifth entry, `How store pages work`, pointed at `/about` — which
            is also `About us` in `Us` below. Two links to one page in one
            footer make a reader decide which of them is the real one.
          */
        ],
      },
      /*
        **There is no `Creators` column either, and it went the same way the
        `Studios` one did.**

        It held `Work with us` and `Your board`, and neither belonged in the
        footer of a shop. `Your board` is a workplace — `topNav` puts it in the
        bar for an approved creator and nobody else, which is the only place a
        link to it can be honest about who it is for; in the footer it was
        offered to every visitor on every page, and for almost all of them it
        opens onto a refusal. `Work with us` is a pitch, and the same argument
        that kept `Publishing` out of the bar applies to a footer seat as well:
        it is read once, before a decision.

        The page itself stays and is still reached from the developers' footer,
        where somebody has already self-selected into that side of the product.
        A recruiting column on the way out of a shop is furniture around a door
        the reader did not come here to find.
      */
      {
        title: 'Us',
        items: [
          { href: '/about', label: 'About us' },
          { href: '/brand', label: 'Our logo' },
          { href: '/contact', label: 'Say hello' },
        ],
      },
    ],
  },

  /**
   * A creator's site is a workplace, so its footer is about the work: what is
   * available to promote, and what the terms are.
   *
   * The board itself is in the bar rather than here, and only for an approved
   * creator — rates on it are commercially sensitive to developers, so an
   * applied creator has the profile and not the board. See `topNav`.
   */
  creators: {
    footerLine: 'Show our games. Get paid.',
    columns: [
      {
        title: 'Your work',
        items: [{ href: '/board', label: 'Your board' }],
      },
      {
        title: 'The deal',
        items: [{ href: '/creators', label: 'How it works' }],
      },
      {
        title: 'GOODBUILD',
        // `For studios` was here and pointed at the pitch page (#142). A
        // creator's footer is the last place that offer belonged even while it
        // stood, and it is a dead link now.
        items: [{ href: '/', label: 'Our games' }],
      },
      {
        title: 'Us',
        items: [
          { href: '/about', label: 'About us' },
          { href: '/brand', label: 'Our logo' },
          { href: '/contact', label: 'Say hello' },
        ],
      },
    ],
  },

  /**
   * The developers' ground, which since #141 is **our own** rather than a
   * prospective partner's — and since #144 has nothing of its own on it.
   *
   * The footer used to be an honest account of what publishing with us cost,
   * because the audience was a studio deciding whether to sign. There is no
   * such studio: we make the games. The `Publishing` column went with the page
   * it pointed at (#142), and the `Your studio` column went with the portal
   * (#144) — `/portal` is not a route any more, and a footer link to it would
   * be the storefront answering a path this app no longer serves.
   *
   * The ground itself stays. `AUDIENCE_PATHS` still paints anything under
   * `/portal` in the developers' blue, and `resolveSite` still answers with it
   * — so the day there is a workshop surface again it has a footer, rather than
   * a missing key being discovered at render time.
   */
  developers: {
    footerLine: 'Where the games get made.',
    columns: [
      {
        title: 'GOODBUILD',
        items: [
          { href: '/', label: 'Our games' },
          { href: '/creators', label: 'For creators' },
        ],
      },
      {
        title: 'Us',
        items: [
          { href: '/about', label: 'About us' },
          { href: '/brand', label: 'Our logo' },
          { href: '/contact', label: 'Say hello' },
        ],
      },
    ],
  },
};

/**
 * One of the two audiences that comes here to work, rather than to shop.
 *
 * The store is not one of them and that is the whole distinction: a player
 * arrives for a game and leaves, and a studio or a creator arrives for a
 * workspace they will be in for an hour. Only the second shape earns a
 * navigation down the side of the page.
 */
export type WorkSite = Exclude<SiteId, 'store'>;

export function isWorkSite(site: SiteId): site is WorkSite {
  return site !== 'store';
}

/**
 * A set of screens that earns a rail, which is not the same list as the sites.
 *
 * **The obvious move was a fourth `SiteId`, and it is the wrong one.** A
 * `SiteId` in this codebase is a hostname with a ground under it, and the
 * hostname is only the first of five things it drags along: an entry in `SITES`
 * so the host resolves, a branch in `resolveSite`, a footer in `SITE_CHROME`, a
 * `[data-gb-space]` block in `tokens.css` holding a channel's worth of
 * colour, and a favicon and OG card generated for it by `tools/brand-mark.mjs`.
 * The staff panel wants none of those. It has no hostname — it is served from
 * `app.goodbuild.cc` alongside everything else — and it deliberately borrows
 * the ground of whichever host you arrived on, which is the behaviour
 * `audienceForPath` already spells out by answering `null` for `/admin`:
 * somebody who came in on `developers.goodbuild.cc` stays in Build while they
 * work, because they are still the same person who arrived there.
 *
 * So the rail is keyed on its own thing. The ground answers *whose colours is
 * this page in*; the rail answers *which set of screens am I inside*, and #111
 * is the first time those two have had different answers. Keeping them one
 * type would have meant inventing a fourth brand channel to get a menu.
 */
export type Workplace = WorkSite | 'admin';

/**
 * Where the staff panel lives, as a prefix rather than a list of its screens.
 *
 * One constant because `workplaceFor` is the only thing that may decide this,
 * and because the prefix is matched rather than compared: `/admin/ledger` is
 * the staff panel and so is `/admin`, and `/administration` — a path nothing
 * serves today and something might — is not either of them.
 */
const ADMIN_PREFIX = '/admin';

/**
 * Which rail this page gets, from the ground it is painted on and its path.
 *
 * Two inputs because two different questions have to be answered at once, and
 * the ground alone gets one of them wrong. On `developers.goodbuild.cc/admin`
 * the ground is Build — correctly, see `Workplace` — and the screens are the
 * staff panel's, so a rail chosen from the ground would offer an administrator
 * the developer portal's menu while they were reading the review queue.
 *
 * **The path wins.** It is the more specific fact: a ground is a property of
 * how somebody arrived and a path is a property of what they are looking at,
 * and a navigation is about the second. Everywhere else the ground is the
 * answer, because `/onboarding` is one route that renders either audience and
 * only the page knows which — which is exactly why this takes the ground rather
 * than deriving everything from the path.
 *
 * `null` means no rail at all, which is the store: a player arrives for a game
 * and leaves. `WORK_NAV` has no key for it, so the null is what stops a lookup
 * being attempted rather than a length being checked afterwards.
 *
 * The path is a pathname — no query, no hash. Every caller has one: `Page`
 * reads `window.location.pathname` and hands it to `SiteChrome` as
 * `currentPath`, which is the same string the rail marks the current entry
 * with. Absent means *not known*, and falls back to the ground.
 */
export function workplaceFor(space: SiteId, path?: string | undefined): Workplace | null {
  if (path === ADMIN_PREFIX || path?.startsWith(`${ADMIN_PREFIX}/`)) return 'admin';
  return isWorkSite(space) ? space : null;
}

export interface NavSection {
  /** A short mono label above the group. */
  title: string;
  items: readonly NavItem[];
}

/**
 * What the rail holds, when the surface's own list is not the answer.
 *
 * `WORK_NAV` below is a constant, and for the developer portal that turned out
 * to be a claim rather than a fact (#88): the studio menu is only true once
 * there *is* a studio being looked at, and somebody with two organisations and
 * neither of them chosen was being offered `Statistics` and `Builds` for a
 * studio nobody had named. The same shape of mistake, one audience over, is a
 * creator who has not applied yet reading a navigation of screens the API
 * refuses them (#90).
 *
 * So a surface may hand the chrome a function instead. It is called with the
 * ground actually being painted — not with the site, and not with a guess made
 * from the path, because `/onboarding` is one route that renders either
 * audience and only the page knows which — and whatever it answers is the rail.
 *
 * **An empty array means no rail at all**, and that is deliberately how "hide
 * it" is spelled rather than a second `null` state. A navigation with nothing
 * in it and a navigation that should not be drawn are the same fact, and one
 * way of saying it cannot drift from the other.
 *
 * A surface that passes nothing gets `WORK_NAV`, which is every surface that
 * has no opinion — the storefront, a test, and anything rendered without an
 * `AccountProvider`.
 *
 * **It is called with a `Workplace` rather than a `WorkSite` since #111**, and
 * the argument is named `place` rather than `site` to stop the old reading
 * coming back: the staff panel is a third set of screens and it is not a third
 * hostname. See `Workplace` for why those are two questions.
 */
export type WorkRail = (place: Workplace) => readonly NavSection[];

/**
 * The vertical navigation on a work surface.
 *
 * **The bar across the top says which side of the product you can reach; this
 * says where you are inside it.** `topNav` gives a studio one entry —
 * `Developers`, to the portal — and that is the right depth for a bar somebody
 * reads on a store page as well as on a build log. Half of a developer's own
 * screens — statistics, builds, payouts — were in no navigation at all,
 * reachable by typing the path or by a table of links the portal drew for
 * itself because nothing else would.
 *
 * So the two audiences get a second navigation, down the left, holding their
 * own screens and nobody else's. The top bar carried Games, Publishing, Library
 * and Orders when this was written and was frankly the players' furniture; it
 * is one bar for everybody now, and the argument for a second one down the side
 * survives the change intact — a bar with room for five links cannot also be a
 * table of contents for a workplace.
 *
 * **Sections rather than a flat list**, because these are two different kinds
 * of thing: the screens you work in every day, and the account setup you touch
 * twice. A flat list of five puts "Payouts" beside "Builds" as though they were
 * the same order of task. The developers' list is down to the first kind since
 * #143 took the studio payout screen away, and the shape stays — it is what the
 * creators' rail and the staff panel are still built on.
 *
 * Every entry is a path this app serves and a route that exists —
 * `test/site-chrome.test.ts` sweeps this the same way it sweeps `SITE_CHROME`,
 * because a nav down the side of every page is a worse place for a dead link
 * than a footer is.
 *
 * **A screen only reachable by typing its path is not a screen** — the mirror
 * of that rule, and the one this list keeps breaking. Two of the developer's
 * own pages were in that state: `/portal/organisation` until #108 and
 * `/portal/agreements` until #109, both of them named in prose by screens that
 * could not link to them. That is the shape a missing rail entry takes before
 * anybody works out what they are looking at.
 *
 * The creators' list is short, and short is the honest answer: the board is
 * what the programme currently offers. Padding it with links to screens that do
 * not exist would make the navigation the least trustworthy thing on the page.
 *
 * **And the staff panel is here now too (#111)**, keyed on `Workplace` rather
 * than on a fourth site — see the type for why the admin rail is not a fourth
 * ground. Eleven screens were registered in the router and reachable through a
 * hand-written table on `/admin` and nowhere else, so leaving that page meant
 * losing every other one: an administrator moving between the curation queue
 * and the ledger went back to the overview each time, and the queue counts they
 * had just read were on the page they had to leave. That table is now derived
 * from this constant rather than maintained beside it, which is the second half
 * of the fix: `/admin/agreements` was a registered route with a working screen
 * that *nothing in the product linked to*, because the overview's table was a
 * second list of routes and nobody swept it. `test/site-chrome.test.ts` sweeps
 * it in both directions now, which is what makes the sweep outlive the screen.
 */
export const WORK_NAV: Readonly<Record<Workplace, readonly NavSection[]>> = {
  /*
    The developers' rail was here and is empty (#144).

    It held one group, `Your studio`, with four entries — `Games`, `Contracts`,
    `Statistics`, `Builds` and `Organisation` — and every one of them opened a
    screen about a company that owned games we sold for it. There is no such
    company: the catalogue is ours and it is administered from `/admin`.

    **The key stays, with an empty list.** `Workplace` is a union of three and
    `AUDIENCE_PATHS` still paints `/portal`-shaped paths in the developers'
    blue if any are ever added back; a missing key would be a lookup returning
    `undefined` in `rail.ts` rather than a rail that draws nothing. Drawing
    nothing is what an empty list already means there, and it is what this
    should mean.
  */
  developers: [],
  creators: [
    {
      title: 'Your work',
      items: [{ href: '/board', label: 'Board' }],
    },
    {
      title: 'Account',
      items: [{ href: '/onboarding', label: 'Payouts' }],
    },
  ],

  /**
   * The staff panel (#111).
   *
   * Three groups rather than one list of eleven, on the same argument the two
   * audiences above get sections for and with more to it here: an
   * administrator's screens divide by *what going there means*, and a flat
   * eleven would put `Ledger` — where money is reconciled and the one page that
   * can say the books are wrong — in the same order of thing as `People`.
   *
   *   `The desk`     what is waiting for a decision today. This is the shift:
   *                  somebody opens the panel, works down these, and is done.
   *   `The platform` what the platform *is*, rather than what is queued on it.
   *                  Looked at to answer a question, not worked through.
   *   `Money`        stated apart because it is the half where a mistake is not
   *                  recoverable by editing a row. Hard rule 2's ledger is the
   *                  last entry in the last group, which is where the least
   *                  casually-clicked thing on the panel belongs.
   */
  admin: [
    {
      title: 'The desk',
      items: [
        /*
         * The overview, and it keeps the generic label the other two audiences'
         * first entries have just lost (#109). `Games` was the right rename
         * there because the portal's front page *is* the studio's titles; this
         * one genuinely is an overview — queue counts, the unbalanced-
         * transaction alarm, and no single subject of its own.
         */
        { href: '/admin', label: 'Overview' },
        /*
          Three entries were here and all three are gone.

          `Review queue` (#112) was everything waiting on a decision in one
          list, above the queues it summarised, so that *what is waiting* could
          be answered without opening four screens to find three of them empty.
          `Publishing` read the pitch inbox (#142). `Curation` listed titles
          waiting to be read.

          Each of their sources has since been closed: nothing registers itself,
          nothing is pitched to us, and titles are created here rather than
          submitted. A desk entry that never has anything on it teaches
          everybody to stop opening it, including on the day something does
          arrive somewhere else.

          `Studios` was the fourth and went with the studios (#144).

          `Creators` is what is left, and it is the reason `The desk` is still a
          group rather than being folded into `The platform`: it is the only
          surface anywhere that can approve a creator application, which is a
          thing that does still arrive unasked because `/onboarding/creator` is
          open to anybody.
        */
        { href: '/admin/creators', label: 'Creators' },
      ],
    },
    {
      title: 'The platform',
      items: [
        { href: '/admin/people', label: 'People' },
        { href: '/admin/games', label: 'Games' },
        { href: '/admin/gifts', label: 'Gifts' },
        { href: '/admin/tokens', label: 'Build tokens' },
        /*
          `Contracts` and `Contract templates` were here, and went with the
          studios (#144). The first was every publishing agreement and where
          each had got to; the second was the blank paper they were drawn from.

          `Contracts` is also the entry that made the reverse sweep in
          `test/site-chrome.test.ts` worth writing: it had been a registered
          route with a working screen and *no link to it anywhere in the
          product*, because the overview's table of screens was a second,
          hand-written list of routes that nobody swept. That test outlives the
          entry it was written for.
        */
      ],
    },
    {
      title: 'Money',
      items: [
        { href: '/admin/refunds', label: 'Refunds' },
        /*
          `Payouts` was here and is gone. It reported how many creator payout
          recipients could not be paid yet — a figure counted off the ledger,
          which has been removed — and it ran no payout itself: moving money was
          always a deliberate act rather than something done from a list.
        */
        /*
          `Ledger` was the last entry in this group and is gone with the books.
          It opened every account's balance, summed from entries, and was the
          least casually-clicked thing on the panel. GOODBUILD sells its own
          games through Creem now; there is no second set of books, and no
          screen in this product can say whether the provider's are right.
        */
      ],
    },
  ],
};

/**
 * Legal links.
 *
 * Kept separate from the site map because they are an obligation rather than a
 * navigation choice, and because a missing one is a compliance problem rather
 * than a broken link.
 *
 * One canonical copy, served by the storefront and reached from every site. The
 * terms that govern a sale do not change depending on which of our hosts you
 * read them on, and three copies would eventually disagree.
 *
 * **It is a footer column now rather than a row along the bottom edge**, and
 * this is still the only copy of the list. `SiteFooter` appends it as a
 * `FooterColumn` titled `Legal`, which is why it stays out of `SITE_CHROME`:
 * put there it would be three identical arrays that have to be kept identical,
 * on the one list where a site drifting from the others is a compliance
 * problem. One list, appended once, and every site gets the same column without
 * anybody remembering to add it.
 *
 * The row it used to be sat beside the social links in the colophon, at label
 * size, in the muted ramp — the same treatment as a copyright line. These are
 * the documents a buyer agrees to, and the refunds page in particular is one
 * somebody comes to the footer *looking for*. A column of them, named, at the
 * size every other footer link is set in, is what a reader searching for the
 * terms actually scans for.
 *
 * **This used to come through `legalNavFor(site)`**, which made the hrefs
 * absolute on every site except the store. Deleted rather than kept: the origin
 * it built them against was `siteUrl`'s, which is a production hostname or a
 * dev port nothing serves — and `chromeHref` already does the same job from the
 * origin the renderer actually knows. One resolver, applied to every entry.
 */
export const LEGAL_NAV: readonly NavItem[] = [
  { href: '/legal/terms', label: 'Terms' },
  { href: '/legal/privacy', label: 'Privacy' },
  { href: '/legal/refunds', label: 'Refunds' },
];
